import { cn } from '@/lib/utils';

export interface PageTabBarTab<T extends string> {
  value: T;
  label: string;
  count?: number;
  /** Draw a non-zero count in the attention colour (e.g. "Needs Reply"). */
  urgent?: boolean;
}

/**
 * The full-width view strip across the top of a page — the Inbox's
 * Needs Reply / In Conversation / Cold / All and the Customers page's
 * Leads / Customers / All. Text tabs on a card, the active one on a tinted
 * ground, counts as badges. One component so the two pages cannot drift.
 */
export function PageTabBar<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: readonly PageTabBarTab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the tab list. */
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex gap-1 overflow-x-auto rounded-xl border bg-card p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.value === value;
        const urgent = tab.urgent && (tab.count ?? 0) > 0;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.value)}
            className={cn(
              'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              selected
                ? 'bg-primary/10 font-semibold text-primary'
                : 'font-medium text-foreground hover:bg-muted'
            )}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span
                className={cn(
                  'min-w-5 rounded-full px-1.5 py-px text-center font-semibold text-xs tabular-nums',
                  urgent
                    ? 'bg-destructive text-white'
                    : selected
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
